<template>
  <fullscreen-dialog v-model="model" :title="dialogTitle" hide-footer icon="mdi-history">
    <v-card flat>
      <v-card-text>
        <!-- User Info Summary -->
        <v-card outlined class="mb-4" v-if="user">
          <v-card-text>
            <v-row align="center">
              <v-col cols="12" md="3">
                <div class="d-flex align-center">
                  <v-icon size="48" color="primary" class="mr-3"
                    >mdi-account-circle</v-icon
                  >
                  <div>
                    <div class="text-h6">{{ toLatin(user.user_name) }}</div>
                    <div class="text-caption">ID: {{ user.user_id }}</div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" md="9">
                <v-row justify="space-between" align="center">
                  <v-col cols="6" sm="auto">
                    <div class="text-center">
                      <div class="text-caption text--secondary">
                        {{ $t("current_balance") }}
                      </div>
                      <div class="text-h6 success--text font-weight-bold">
                        {{ formatCurrency(currentBalance) }}
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="6" sm="auto">
                    <div class="text-center">
                      <div class="text-caption text--secondary">
                        {{ $t("total_topups") }}
                      </div>
                      <div class="text-h6 primary--text font-weight-bold">
                        {{ topupHistory.length }}
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="6" sm="auto">
                    <div class="text-center">
                      <div class="text-caption text--secondary">
                        {{ $t("total_added") }}
                      </div>
                      <div class="text-h6 blue--text font-weight-bold">
                        {{ formatCurrency(totalAdded) }}
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="6" sm="auto">
                    <div class="text-center">
                      <div class="text-caption text--secondary">
                        {{ $t("total_spent") }}
                      </div>
                      <div class="text-h6 error--text font-weight-bold">
                        {{ formatCurrency(Math.abs(totalSpent)) }}
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" md="auto">
                    <primary-button @click="addCredit">
                      <v-icon class="mr-2">mdi-cash-plus</v-icon>
                      {{ $t("add_credit") }}
                    </primary-button>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- History Table -->
        <v-card outlined>
          <v-card-title>
            <v-icon left>mdi-format-list-bulleted</v-icon>
            {{ $t("topup_history") }}
            <v-spacer></v-spacer>
            <autocomplete
              v-model="selectedRestaurantIds"
              :items="companyRestaurants"
              :item-text="(item) => toLatin(item.name)"
              item-value="id"
              :label="$t('restaurant')"
              multiple
              chips
              small-chips
              deletable-chips
              clearable
              hide-details
            />
          </v-card-title>
          <v-divider></v-divider>
          <v-data-table
            :headers="headers"
            :items="topupHistoryWithTotal"
            hide-default-footer
            disable-pagination
            class="elevation-0"
          >
            <template v-slot:item.num="{ item }">
              <span :class="item.isTotal ? 'font-weight-bold' : ''">
                {{ item.num }}
              </span>
            </template>

            <template v-slot:item.date="{ item }">
              <span :class="item.isTotal ? 'font-weight-bold' : ''">
                {{ item.isTotal ? $t("total").toUpperCase() : localeDate(item.date) }}
              </span>
            </template>

            <template v-slot:item.amount="{ item }">
              <span
                :class="
                  item.isTotal
                    ? 'font-weight-bold primary--text'
                    : item.amount >= 0
                    ? 'font-weight-bold success--text'
                    : 'font-weight-bold error--text'
                "
              >
                {{ item.isTotal ? "" : item.amount >= 0 ? "+" : "" }}{{ formatCurrency(item.amount) }}
              </span>
            </template>

            <template v-slot:item.notes="{ item }">
              <span v-if="!item.isTotal" class="text--secondary">
                {{ item.notes }}
              </span>
            </template>

            <template v-slot:item.restaurant_label="{ item }">
              <span v-if="!item.isTotal" :class="item.restaurant_id ? '' : 'text--secondary'">
                {{ item.restaurant_id ? item.restaurant_name : $t('all_restaurants') }}
              </span>
            </template>
          </v-data-table>
        </v-card>
      </v-card-text>
    </v-card>
  </fullscreen-dialog>
</template>

<script>
import hasModel from "@/mixins/hasModel";
import FullscreenDialog from "@/components/dialogs/FullscreenDialog.vue";

export default {
  name: "CreditUserHistoryDialog",
  components: {
    FullscreenDialog,
  },
  mixins: [hasModel],
  props: {
    user: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      selectedRestaurantIds: [],
    };
  },
  computed: {
    dialogTitle() {
      return this.user
        ? `${this.$t("topup_history")} - ${this.toLatin(this.user.user_name)}`
        : this.$t("topup_history");
    },
    currentBalance() {
      return this.user?.totals?.current_credit_balance ?? 0;
    },
    headers() {
      return [
        { text: this.$t("r_b"), value: "num", sortable: false, width: "80px" },
        { text: this.$t("date"), value: "date", sortable: true },
        { text: this.$t("restaurant"), value: "restaurant_label", sortable: false },
        { text: this.$t("amount"), value: "amount", sortable: false, align: "right" },
        { text: this.$t("note"), value: "notes", sortable: false },
      ];
    },
    companyRestaurants() {
      return this.$store.state.company?.restaurants || [];
    },
    topupHistory() {
      if (!this.user?.creditTransactions?.length) return [];
      const sel = this.selectedRestaurantIds;
      const source =
        sel.length > 0 && sel.length < this.companyRestaurants.length
          ? this.user.creditTransactions.filter((t) => sel.includes(t.restaurant_id))
          : this.user.creditTransactions;
      const filtered = source;
      return [...filtered]
        .sort((a, b) => new Date(b.date || b.created_at) - new Date(a.date || a.created_at))
        .map((t, index) => ({
          id: t.id,
          num: index + 1,
          date: t.date || t.created_at || null,
          restaurant_id: t.restaurant_id ?? null,
          restaurant_name: this.toLatin(t.restaurant?.name || ""),
          amount: this.parseAmount(t.total_amount ?? t.price),
          notes: this.toLatin(t.notes || ""),
          isTotal: false,
        }));
    },
    topupHistoryWithTotal() {
      const history = [...this.topupHistory];
      if (history.length > 0) {
        const totalAmount = history.reduce((sum, item) => sum + item.amount, 0);
        history.push({
          id: "total",
          num: "",
          date: "",
          amount: totalAmount,
          notes: "",
          isTotal: true,
        });
      }
      return history;
    },
    totalAdded() {
      return this.topupHistory
        .filter((t) => t.amount > 0)
        .reduce((sum, t) => sum + t.amount, 0);
    },
    totalSpent() {
      return this.topupHistory
        .filter((t) => t.amount < 0)
        .reduce((sum, t) => sum + t.amount, 0);
    },
  },
  created() {
    this.selectedRestaurantIds = this.companyRestaurants.map((r) => r.id);
  },
  watch: {
    user() {
      this.selectedRestaurantIds = this.companyRestaurants.map((r) => r.id);
    },
    model(val) {
      if (val) {
        this.selectedRestaurantIds = this.companyRestaurants.map((r) => r.id);
      }
    },
    companyRestaurants(val) {
      if (this.selectedRestaurantIds.length === 0 && val.length > 0) {
        this.selectedRestaurantIds = val.map((r) => r.id);
      }
    },
  },
  methods: {
    parseAmount(value) {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    },
    formatCurrency(amount) {
      if (amount === null || amount === undefined || amount === "") return "0.00 " + (this.restaurantCurrency || "");
      return parseFloat(amount).toFixed(2) + " " + (this.restaurantCurrency || "");
    },
    addCredit() {
      this.model = false;
      this.$emit("navigate-to-topup", this.user.user_id);
    },
  },
};
</script>
